import React, {useState, useEffect} from 'react';
import {useParams, useNavigate} from 'react-router';
import {
    Container,
    Typography,
    Box,
    Tabs,
    Tab,
    CircularProgress,
    Alert,
    Breadcrumbs,
    Link,
    Grid,
    Paper,
    Stack,
    Chip,
    Divider,
    Button,
} from '@mui/material';
import {
    Home as HomeIcon,
    School as SchoolIcon,
    Groups as GroupsIcon,
    Topic as TopicIcon,
    Quiz as QuizIcon,
    Edit as EditIcon,
} from '@mui/icons-material';
import {createApiClient} from '../../services/ApiClient.jsx';
import TopicsTab from './TopicsTab.jsx';
import CourseMaterialsTab from './CourseMaterialsTab.jsx';
import CourseTeachersTab from './CourseTeachersTab.jsx';
import UpdatePopup from '../../components/UpdatePopup.jsx';

const getAcademicLevelLabel = (level) => {
    const labels = {
        'bachelor_1': 'Bachelor 1. Sem',
        'bachelor_2': 'Bachelor 2. Sem',
        'bachelor_3': 'Bachelor 3. Sem',
        'bachelor_4': 'Bachelor 4. Sem',
        'bachelor_5': 'Bachelor 5. Sem',
        'bachelor_6': 'Bachelor 6. Sem',
        'master_1': 'Master 1. Sem',
        'master_2': 'Master 2. Sem',
        'master_3': 'Master 3. Sem',
        'master_4': 'Master 4. Sem'
    };
    return labels[level] || level;
};

const StatCard = ({icon, value, label, color, loading}) => (
    <Paper
        elevation={0}
        sx={{
            p: 2,
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            textAlign: 'center',
            height: '80px',
            minWidth: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
                borderColor: color,
                transform: 'translateY(-2px)',
                boxShadow: 1,
            }
        }}
    >
        <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
            {React.cloneElement(icon, {
                sx: {fontSize: 20, mb: 0.5, color: color}
            })}
            {loading ? (
                <CircularProgress size={16}/>
            ) : (
                <Typography variant="h6" sx={{fontWeight: 'bold', color: 'text.primary', fontSize: '1.1rem'}}>
                    {value.toLocaleString()}
                </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{fontSize: '0.75rem'}}>
                {label}
            </Typography>
        </Box>
    </Paper>
);

const TeacherCourseDashboard = ({accessToken, claims}) => {
    const {courseId} = useParams();
    const navigate = useNavigate();
    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [tabValue, setTabValue] = useState(0);
    const [showEditCourse, setShowEditCourse] = useState(false);

    const apiClient = createApiClient(accessToken);

    const fetchCourse = async () => {
        try {
            const courseResponse = await apiClient.get(`/api/courses/${courseId}`);
            setCourse(courseResponse);
            setError(null);
        } catch (err) {
            console.error('Error fetching course:', err);
            setError('Failed to load course. Please try again later.');
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                await fetchCourse();
            } catch (err) {
                console.error('Error fetching course dashboard data:', err);
                setError('Failed to load course dashboard. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        if (accessToken && courseId) fetchData();
    }, [accessToken, courseId]);

    const handleTabChange = (_, newValue) => setTabValue(newValue);

    const handleCourseUpdate = async (updatedCourse) => {
        // Update the local course state with the updated data
        if (updatedCourse) {
            setCourse(updatedCourse);
        } else {
            // Fallback: refetch the course data
            await fetchCourse();
        }
    };

    const handleEditCourse = () => {
        setShowEditCourse(true);
    };

    const handleCloseEditCourse = () => {
        setShowEditCourse(false);
    };

    if (loading) {
        return (
            <Container maxWidth="xl" sx={{mt: 4, mb: 8}}>
                <Box sx={{display: 'flex', justifyContent: 'center', my: 4}}>
                    <CircularProgress/>
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="xl" sx={{mt: 4, mb: 8}}>
                <Alert severity="error" sx={{mb: 4}}>
                    {error}
                </Alert>
            </Container>
        );
    }

    if (!course) {
        return (
            <Container maxWidth="xl" sx={{mt: 4, mb: 8}}>
                <Alert severity="warning" sx={{mb: 4}}>
                    Course not found.
                </Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{mt: 2, mb: 10}}>
            {/* Breadcrumbs */}
            <Breadcrumbs aria-label="breadcrumb" sx={{mb: 1}}>
                <Link
                    color="inherit"
                    href="/teacher/dashboard"
                    sx={{display: 'flex', alignItems: 'center'}}
                >
                    <HomeIcon sx={{mr: 0.5}} fontSize="inherit"/>
                    Dashboard
                </Link>
                <Typography
                    color="text.primary"
                    sx={{display: 'flex', alignItems: 'center'}}
                >
                    <SchoolIcon sx={{mr: 0.5}} fontSize="inherit"/>
                    {course.title}
                </Typography>
            </Breadcrumbs>

            {/* Divider between breadcrumbs and header */}
            <Divider sx={{mb: 4}}/>

            {/* Course Header with Title/Edit + Description/Stats */}
            <Box sx={{mb: 4}}>
                {/* Title + Edit button */}
               <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mb: 1}}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="h4">{course.title}</Typography>
                    {course.academic_level && (
                        <Chip
                            icon={<SchoolIcon />}
                            label={getAcademicLevelLabel(course.academic_level)}
                            color="primary"
                            variant="outlined"
                            size="medium"
                        />
                    )}
                </Stack>
                <Button
                    variant="outlined"
                    startIcon={<EditIcon/>}
                    size="small"
                    onClick={handleEditCourse}
                >
                    Edit
                </Button>
            </Stack>

                {/* Description on left, stats chips on right */}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    {course.description ? (
                        <Typography variant="subtitle1" color="text.secondary">
                            {course.description}
                        </Typography>
                    ) : <Box/>}

                    <Stack direction="row" spacing={1}>
                        <Chip
                            icon={<GroupsIcon/>}
                            label={`${course.student_count.toLocaleString()} Students`}
                            variant="outlined"
                            size="small"
                        />
                        <Chip
                            icon={<TopicIcon/>}
                            label={`${course.topic_count.toLocaleString()} Topics`}
                            variant="outlined"
                            size="small"
                        />
                        <Chip
                            icon={<QuizIcon/>}
                            label={`${course.question_count.toLocaleString()} Questions`}
                            variant="outlined"
                            size="small"
                        />
                    </Stack>
                </Stack>
            </Box>

            {/* Tabs */}
            <Box sx={{mt: 4}}>
                <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
                    <Tabs value={tabValue} onChange={handleTabChange} aria-label="course tabs">
                        <Tab label="Topics"/>
                        <Tab label="Course Materials"/>
                        <Tab label="Teachers"/>
                    </Tabs>
                </Box>

                <Box sx={{mt: 3}}>
                    {tabValue === 0 && (
                        <TopicsTab
                            courseId={courseId}
                            apiClient={apiClient}
                            onUpdate={handleCourseUpdate}
                        />
                    )}
                    {tabValue === 1 && (
                        <CourseMaterialsTab
                            courseId={courseId}
                            apiClient={apiClient}
                        />
                    )}
                    {tabValue === 2 && (
                        <CourseTeachersTab
                            courseId={courseId}
                            apiClient={apiClient}
                        />
                    )}
                </Box>
            </Box>

            {/* Edit Course Popup */}
            <UpdatePopup
                open={showEditCourse}
                onClose={handleCloseEditCourse}
                onUpdate={handleCourseUpdate}
                entityType="course"
                entityData={course}
                apiClient={apiClient}
                courseId={courseId}
            />
        </Container>
    );
};

export default TeacherCourseDashboard;